import React, { useState, useRef, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CustomButton from '../../../component/elements/CustomButton/CustomButton';
import FilePreview from '../../../component/renderFilePreview';
import TextFieldCustom from '../../../component/elements/textfieldCustom';
import axiosInstance from '../../../configuration/request';
import { useSnackbar } from '../../../component/SnackbarContext';

function ApproveRejectForm({ onClose, approvalDetails, onSubmit }) {
  const [isRejected, setIsRejected] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const reasonRef = useRef(null);
  const showSnackbar = useSnackbar();
  const [userData, setUserData] = useState({});

  useEffect(() => {
    if (approvalDetails) {
      const isBusiness = approvalDetails.type === 'business user';
      const addressObj = approvalDetails.address?.[0] || {};
      const formattedAddress = `${addressObj.house_no || ''}, ${addressObj.landmark || ''}, ${addressObj.city || ''}, ${addressObj.state || ''} ${addressObj.zipcode || ''}`;

      const baseData = {
        name: approvalDetails.name || 'NA',
        email: approvalDetails.email || 'NA',
        phone: approvalDetails.phoneNumber || 'NA',
        type: approvalDetails?.type ? approvalDetails.type.charAt(0).toUpperCase() + approvalDetails.type.slice(1) : 'NA',
        address: formattedAddress || 'NA',
        uploadDocument: approvalDetails.uploadedDocs || []
      };

      if (isBusiness) {
        setUserData({
          ...baseData,
          storeName: approvalDetails.storeName || 'NA',
          storePhone: approvalDetails.store_phone || 'NA',
          licenceNumber: approvalDetails.licenceNumber || 'NA'
        });
      } else {
        setUserData({
          ...baseData,
          vehicleType: approvalDetails.vehicle_type || 'NA',
          vehicleNumber: approvalDetails.vehicle_number || 'NA'
        });
      }
    }
  }, [approvalDetails]);

  const handleUpdateUser = (payload) => {
    axiosInstance.patch(`/customer/users/${approvalDetails.user_id}`, payload)
      .then(() => onSubmit())
      .catch(() => showSnackbar("Something went wrong!", "error"));
  };

  const handleSubmit = () => {
    const payload = {
      action: {
        isApproved: "approved",
        notificationId: approvalDetails.id,
        comment: "Profile is approved"
      }
    };
    handleUpdateUser(payload);
  };

  const handleReject = () => {
    setIsRejected(true);
    setTimeout(() => {
      reasonRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleConfirmRejection = () => {
    if (!reason.trim()) {
      setReasonError('Reason is required');
      return;
    }
    const payload = {
      action: {
        isApproved: "rejected",
        notificationId: approvalDetails.id,
        comment: reason
      }
    };
    handleUpdateUser(payload);
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (e.target.value.trim()) setReasonError('');
  };

  return (
    <div className="mt-6 px-10">
      <div className="flex justify-between mb-6">
        <div className="text-lg font-[500]">Approve / Reject Request</div>
        <button onClick={onClose} aria-label="Close" className="text-gray-600 hover:text-black">
          <CloseIcon />
        </button>
      </div>

      <div className="overflow-y-auto pb-3 scrollbar-hide h-[450px]">
        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {[
            { label: "Name", value: userData.name },
            { label: "Email", value: userData.email },
            { label: "Phone Number", value: userData.phone },
            { label: "Type", value: userData.type },
            userData.storeName && { label: "Store Name", value: userData.storeName },
            userData.storePhone && { label: "Store Phone", value: userData.storePhone },
            userData.licenceNumber && { label: "Licence Number", value: userData.licenceNumber },
            userData.vehicleType && { label: "Vehicle Type", value: userData.vehicleType },
            userData.vehicleNumber && { label: "Vehicle Number", value: userData.vehicleNumber },
          ].filter(Boolean).map((field, idx) => (
            <div key={idx} className="border p-4 rounded-md">
              <label className="block text-sm font-semibold text-[#999999] mb-1">{field.label}</label>
              <div className="text-[#222222]">{field.value}</div>
            </div>
          ))}
          <div className="sm:col-span-2 border p-4 rounded-md">
            <label className="block text-sm font-semibold text-[#999999] mb-1">Address</label>
            <div className="text-[#222222] whitespace-pre-line">{userData.address}</div>
          </div>
        </div>

        {/* Files */}
        <div className="flex flex-wrap gap-4 mb-4">
          {userData.uploadDocument?.length > 0 ? userData.uploadDocument.map((file, idx) => (
            <FilePreview key={idx} file={file} />
          )) : <div className="text-gray-500">No documents uploaded</div>}
        </div>

        {/* Rejection reason */}
        {isRejected && (
          <div className="mt-6" ref={reasonRef}>
            <TextFieldCustom
              value={reason}
              onChange={handleReasonChange}
              placeholder="Reason for rejection*"
              heightClass="custom-textarea-height"
              fullWidth
              multiline
              error={!!reasonError}
              helperText={reasonError}
            />
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row justify-center items-center my-8 gap-3">
        {!isRejected ? (
          <>
            <CustomButton onClick={handleReject} customeClass="primary-custom-button w-full sm:w-[160px] h-[40px]">
              Reject
            </CustomButton>
            <CustomButton onClick={handleSubmit} customeClass="gradient-button w-full sm:w-[160px] h-[40px]">
              Approve
            </CustomButton>
          </>
        ) : (
          <>
            <CustomButton onClick={onClose} customeClass="primary-custom-button w-full sm:w-[160px] h-[40px]">
              Cancel
            </CustomButton>
            <CustomButton onClick={handleConfirmRejection} customeClass="gradient-button w-full sm:w-[160px] h-[40px]">
              Confirm Rejection
            </CustomButton>
          </>
        )}
      </div>
    </div>
  );
}

export default ApproveRejectForm;
